import { useEffect, useState } from "react";
import { loyaltyJoin, loyaltyLookup, fetchRelatedItems, fetchPickupSlots } from "@/lib/api";
import { formatMoney } from "@/lib/orderUtils";

// =====================================================
// Loyalty panel — sign in / join + show points / redeem
// =====================================================
export const LoyaltyPanel = ({ member, setMember, redeemPoints, setRedeemPoints, subtotal, otherDiscount = 0 }) => {
    const [mode, setMode] = useState("idle"); // idle, signin, join
    const [phone, setPhone] = useState("");
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [birthday, setBirthday] = useState("");
    const [referralInput, setReferralInput] = useState("");
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);
    const [copied, setCopied] = useState(false);

    // Prefill referral code if the URL has ?ref=XXXXXX (customer arrives via a shared link).
    useEffect(() => {
        try {
            const p = new URLSearchParams(window.location.search).get("ref");
            if (p) setReferralInput(p.toUpperCase());
        } catch { /* ignore */ }
    }, []);

    const signIn = async () => {
        if (!phone && !email) { setErr("Phone or email"); return; }
        setBusy(true); setErr("");
        try {
            const m = await loyaltyLookup(phone, email);
            setMember(m);
            setMode("idle");
        } catch {
            setErr("No member found — tap Join instead");
        } finally { setBusy(false); }
    };
    const join = async () => {
        if (!name || (!phone && !email)) { setErr("Name + phone or email"); return; }
        setBusy(true); setErr("");
        try {
            const m = await loyaltyJoin(name, phone, email, birthday || null, referralInput.trim().toUpperCase() || null);
            setMember(m);
            setMode("idle");
        } catch (ex) {
            setErr(ex.response?.data?.detail || "Failed");
        } finally { setBusy(false); }
    };

    const shareReferral = async () => {
        if (!member?.referral_code) return;
        const shareUrl = `${window.location.origin}${window.location.pathname}?ref=${member.referral_code}`;
        const text = `Join me on Naami Kitchens and we both get £5 off. Use code ${member.referral_code} → ${shareUrl}`;
        try {
            if (navigator.share) {
                await navigator.share({ title: "Naami Kitchens", text, url: shareUrl });
            } else {
                await navigator.clipboard.writeText(text);
                setCopied(true);
                setTimeout(() => setCopied(false), 2200);
            }
        } catch { /* user cancelled */ }
    };

    if (member) {
        const blocks = Math.floor(member.points / 100);
        const maxRedeem = Math.min(blocks * 100, Math.floor(subtotal / 5) * 100);
        const tier = member.tier || { name: "Bronze", multiplier: 1.0, color: "#cd7f32", next_name: null, next_pence: 0 };
        const nextPct = tier.next_pence > 0
            ? Math.min(100, Math.round((member.lifetime_spend / (member.lifetime_spend + tier.next_pence / 100)) * 100))
            : 100;
        const bonusThisOrder = Math.round(Math.max(0, subtotal - redeemPoints * 0.05 - otherDiscount) * tier.multiplier);
        return (
            <div className="border border-[var(--gold)]/40 bg-[var(--gold)]/5 p-4 mb-6" data-testid="loyalty-panel-active">
                <div className="flex items-center justify-between gap-3 mb-2">
                    <div>
                        <p className="font-mono text-[9px] uppercase tracking-[0.22em] text-gold mb-1">// Naami Club</p>
                        <div className="flex items-center gap-2">
                            <p className="font-display text-[18px]">Hello, {member.name}</p>
                            <span
                                className="font-mono text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 border rounded-sm"
                                style={{ borderColor: tier.color, color: tier.color }}
                                data-testid="loyalty-tier-badge"
                            >
                                {tier.name}
                                {tier.multiplier > 1 && <> · {tier.multiplier}×</>}
                            </span>
                        </div>
                    </div>
                    <button
                        onClick={() => { setMember(null); setRedeemPoints(0); }}
                        className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted hover:text-gold"
                        data-testid="loyalty-signout"
                    >
                        Not you?
                    </button>
                </div>
                <div className="flex items-baseline gap-4 mb-3">
                    <span className="font-display text-[28px] text-gold leading-none" data-testid="loyalty-points">{member.points}</span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted">points · £{(member.points * 0.05).toFixed(2)} max</span>
                </div>
                {tier.next_name && (
                    <div className="mb-3" data-testid="loyalty-tier-progress">
                        <div className="h-1 bg-[var(--border)] overflow-hidden">
                            <div className="h-full" style={{ width: `${nextPct}%`, background: tier.color }} />
                        </div>
                        <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-muted mt-1">
                            £{(tier.next_pence / 100).toFixed(2)} to {tier.next_name}
                        </p>
                    </div>
                )}
                {maxRedeem >= 100 ? (
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted">Redeem:</span>
                        {[0, 100, 200, 300].filter((v) => v <= maxRedeem).map((v) => (
                            <button
                                key={v}
                                onClick={() => setRedeemPoints(v)}
                                className={`font-mono text-[10px] uppercase tracking-[0.18em] px-2.5 py-1.5 border ${
                                    redeemPoints === v ? "border-[var(--gold)] bg-[var(--gold)] text-[var(--bg)]" : "border-[var(--border)] hover:border-[var(--gold)]"
                                }`}
                                data-testid={`loyalty-redeem-${v}`}
                            >
                                {v === 0 ? "None" : `−£${(v / 20).toFixed(0)} (${v}pts)`}
                            </button>
                        ))}
                    </div>
                ) : (
                    <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted">
                        Need {100 - member.points} more pts for £5 off
                    </p>
                )}
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted mt-3">
                    + {bonusThisOrder} pts on this order{tier.multiplier > 1 ? ` (${tier.name} ${tier.multiplier}×)` : ""}
                </p>
                {member.referral_code && (
                    <div className="mt-3 pt-3 border-t border-[var(--border)] flex items-center justify-between gap-3">
                        <div>
                            <p className="font-mono text-[9px] uppercase tracking-[0.22em] text-muted mb-0.5">Refer a friend</p>
                            <p className="font-mono text-[13px] tracking-[0.14em] text-gold" data-testid="loyalty-referral-code">{member.referral_code}</p>
                            <p className="text-[10px] text-muted">Both get 100 pts (£5) on their first order</p>
                        </div>
                        <button
                            onClick={shareReferral}
                            className="btn-ghost text-[10px] font-mono uppercase tracking-[0.16em]"
                            data-testid="loyalty-referral-share"
                        >
                            {copied ? "Copied!" : "Share"}
                        </button>
                    </div>
                )}
                {member.birthday_credit && (
                    <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-gold mt-3" data-testid="loyalty-birthday-bonus">
                        Happy birthday! +{member.birthday_credit} pts added
                    </p>
                )}
            </div>
        );
    }

    return (
        <div className="border border-[var(--border)] bg-[var(--surface-elevated)] p-4 mb-6" data-testid="loyalty-panel-anon">
            <p className="font-mono text-[9px] uppercase tracking-[0.22em] text-gold mb-1">// Naami Club</p>
            <p className="font-display text-[16px] mb-1">Earn 1 point per £1 · unlock Silver/Gold perks</p>
            <p className="text-[12px] text-muted mb-3">100 points = £5 off · Birthday bonus · Refer friends for £5 each</p>
            {mode === "idle" && (
                <div className="flex gap-2">
                    <button onClick={() => setMode("signin")} className="btn-ghost flex-1 justify-center" data-testid="loyalty-signin-open">Sign in</button>
                    <button onClick={() => setMode("join")} className="btn-primary flex-1 justify-center" data-testid="loyalty-join-open">Join free</button>
                </div>
            )}
            {(mode === "signin" || mode === "join") && (
                <div className="space-y-2 mt-3">
                    {mode === "join" && (
                        <input className="input" placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} data-testid="loyalty-name" />
                    )}
                    <input className="input" placeholder="Phone (e.g. 07900 123456)" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="loyalty-phone" />
                    <input className="input" type="email" placeholder="Email (optional)" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="loyalty-email" />
                    {mode === "join" && (
                        <>
                            <div>
                                <label className="font-mono text-[9px] uppercase tracking-[0.18em] text-muted block mb-1">Birthday (optional — get £5 credit yearly)</label>
                                <input className="input" type="date" value={birthday} onChange={(e) => setBirthday(e.target.value)} data-testid="loyalty-birthday" />
                            </div>
                            <input
                                className="input"
                                placeholder="Referral code (optional)"
                                value={referralInput}
                                onChange={(e) => setReferralInput(e.target.value.toUpperCase())}
                                maxLength={8}
                                data-testid="loyalty-referral-input"
                            />
                        </>
                    )}
                    {err && <p className="text-[#ff5246] text-xs font-mono">{err}</p>}
                    <div className="flex gap-2">
                        <button onClick={() => setMode("idle")} className="btn-ghost flex-1 justify-center">Cancel</button>
                        <button
                            onClick={mode === "signin" ? signIn : join}
                            disabled={busy}
                            className="btn-primary flex-1 justify-center"
                            data-testid={`loyalty-${mode}-submit`}
                        >
                            {busy ? "…" : mode === "signin" ? "Sign in →" : "Join →"}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

// =====================================================
// Pickup-slot picker — ASAP, 15-min slots in user's local time, custom time
// =====================================================
export const SlotPicker = ({ scheduledFor, setScheduledFor, isOpenAt }) => {
    const [allSlots, setSlots] = useState([]);
    const slots = isOpenAt ? allSlots.filter((s) => isOpenAt(new Date(s.iso))) : allSlots;
    const asapAllowed = !isOpenAt || isOpenAt(new Date());
    const [showCustom, setShowCustom] = useState(false);
    const [customTime, setCustomTime] = useState("");

    useEffect(() => { fetchPickupSlots().then((r) => setSlots(r.slots || [])); }, []);

    const formatLabel = (iso) => {
        try {
            return new Date(iso).toLocaleTimeString([], {
                hour: "2-digit", minute: "2-digit", hour12: false,
            });
        } catch {
            return iso;
        }
    };

    // Convert "HH:MM" picked by user into an ISO string for *today* in local TZ.
    // If the time is earlier than now (e.g. picked midnight after 11pm), roll to tomorrow.
    const applyCustom = (hhmm) => {
        if (!hhmm) return;
        const [h, m] = hhmm.split(":").map((x) => parseInt(x, 10));
        if (isNaN(h) || isNaN(m)) return;
        const d = new Date();
        d.setHours(h, m, 0, 0);
        if (d.getTime() < Date.now()) d.setDate(d.getDate() + 1);
        if (isOpenAt && !isOpenAt(d)) {
            alert("Sorry — that time is outside opening hours. Please pick another time.");
            return;
        }
        setScheduledFor(d.toISOString());
        setShowCustom(false);
    };

    const isCustom = scheduledFor && !slots.some((s) => s.iso === scheduledFor);

    return (
        <div className="mb-6" data-testid="slot-picker">
            <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted block mb-3">
                When would you like to collect?
            </label>
            <div className="flex flex-wrap gap-2">
                <button
                    disabled={!asapAllowed}
                    title={asapAllowed ? undefined : "Closed right now — pick a later time"}
                    onClick={() => { setScheduledFor(null); setShowCustom(false); }}
                    className={`font-mono text-[10px] uppercase tracking-[0.18em] px-3 py-2 border ${
                        scheduledFor === null ? "border-[var(--gold)] bg-[var(--gold)] text-[var(--bg)]" : "border-[var(--border)] hover:border-[var(--gold)]"
                    }`}
                    data-testid="slot-asap"
                    style={asapAllowed ? undefined : { opacity: 0.35, cursor: "not-allowed" }}
                >
                    ⚡ ASAP
                </button>
                {slots.map((s) => {
                    const label = formatLabel(s.iso);
                    return (
                        <button
                            key={s.iso}
                            onClick={() => { setScheduledFor(s.iso); setShowCustom(false); }}
                            className={`font-mono text-[10px] uppercase tracking-[0.18em] px-3 py-2 border ${
                                scheduledFor === s.iso ? "border-[var(--gold)] bg-[var(--gold)] text-[var(--bg)]" : "border-[var(--border)] hover:border-[var(--gold)]"
                            }`}
                            data-testid={`slot-${label}`}
                        >
                            {label}
                        </button>
                    );
                })}
                <button
                    onClick={() => setShowCustom((v) => !v)}
                    className={`font-mono text-[10px] uppercase tracking-[0.18em] px-3 py-2 border ${
                        isCustom || showCustom ? "border-[var(--gold)] bg-[var(--gold)] text-[var(--bg)]" : "border-[var(--border)] hover:border-[var(--gold)]"
                    }`}
                    data-testid="slot-custom"
                >
                    {isCustom ? `✏ ${formatLabel(scheduledFor)}` : "✏ Custom time"}
                </button>
            </div>
            {showCustom && (
                <div className="mt-3 flex items-center gap-2" data-testid="slot-custom-picker">
                    <input
                        type="time"
                        className="input flex-1"
                        value={customTime}
                        onChange={(e) => setCustomTime(e.target.value)}
                        data-testid="slot-custom-input"
                    />
                    <button
                        onClick={() => applyCustom(customTime)}
                        disabled={!customTime}
                        className="btn-secondary disabled:opacity-40"
                        data-testid="slot-custom-apply"
                    >
                        Set time
                    </button>
                </div>
            )}
        </div>
    );
};

// =====================================================
// Cross-sell: "Often ordered with"
// =====================================================
export const CrossSell = ({ lines, onAdd }) => {
    const [related, setRelated] = useState([]);

    useEffect(() => {
        if (!lines || lines.length === 0) { setRelated([]); return; }
        // pull suggestions for the most recently added item
        const lastItem = lines[lines.length - 1];
        let cancelled = false;
        (async () => {
            try {
                const r = await fetchRelatedItems(lastItem.item_id, 6);
                if (cancelled) return;
                const inCart = new Set(lines.map((l) => l.item_id));
                setRelated((r.items || []).filter((x) => !inCart.has(x.item_id)).slice(0, 4));
            } catch { /* silent */ }
        })();
        return () => { cancelled = true; };
    }, [lines]);

    if (related.length === 0) return null;
    return (
        <div className="mb-6" data-testid="cross-sell">
            <p className="eyebrow mb-3">// Customers also ordered</p>
            <div className="grid grid-cols-2 gap-3">
                {related.map((it) => (
                    <button
                        key={it.item_id}
                        onClick={() => onAdd(it)}
                        className="flex items-center gap-3 p-3 border border-[var(--border)] hover:border-[var(--gold)] text-left transition-colors"
                        data-testid={`cross-sell-${it.item_id}`}
                    >
                        {it.image_url && (
                            <img src={it.image_url} alt={it.name} className="w-14 h-14 object-cover shrink-0" loading="lazy" />
                        )}
                        <div className="flex-1 min-w-0">
                            <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-muted mb-0.5">
                                [{it.brand_token}]
                            </div>
                            <div className="text-[13px] font-display truncate" style={{ color: it.brand_accent }}>
                                {it.name}
                            </div>
                            <div className="font-mono text-[11px] text-gold mt-0.5">
                                + {formatMoney(it.price)}
                            </div>
                        </div>
                    </button>
                ))}
            </div>
        </div>
    );
};


// =====================================================
// Naami Club promo banner — big, dismissable "Join for £5 off your first order"
// pinned on kiosk/QR/multibrand landing screens for non-members. Once the
// customer joins (or dismisses), it stays hidden this session via localStorage.
// =====================================================
export const LoyaltyPromoBanner = ({ member, onJoinClick }) => {
    const [dismissed, setDismissed] = useState(false);
    useEffect(() => {
        try { setDismissed(localStorage.getItem("naami_loyalty_banner_dismissed") === "1"); } catch { /* ignore */ }
    }, []);
    if (member || dismissed) return null;
    const dismiss = () => {
        try { localStorage.setItem("naami_loyalty_banner_dismissed", "1"); } catch { /* ignore */ }
        setDismissed(true);
    };
    return (
        <div
            className="relative border border-[var(--gold)]/60 bg-gradient-to-br from-[var(--gold)]/12 via-transparent to-[var(--gold)]/5 p-4 md:p-5 mb-5"
            data-testid="loyalty-promo-banner"
        >
            <button
                onClick={dismiss}
                aria-label="Dismiss"
                className="absolute top-2 right-2 font-mono text-[10px] uppercase tracking-[0.2em] text-muted hover:text-gold"
                data-testid="loyalty-promo-dismiss"
            >
                ×
            </button>
            <p className="font-mono text-[9px] uppercase tracking-[0.24em] text-gold mb-1">// Naami Club</p>
            <p className="font-display text-[20px] md:text-[24px] leading-tight mb-1">Join free, earn points on every order</p>
            <p className="text-[12px] md:text-[13px] text-muted mb-3 max-w-[420px]">
                1 point per £1 · Silver &amp; Gold tiers unlock 1.25× &amp; 1.5× · £5 birthday credit · £5 for every friend you refer.
            </p>
            <button
                onClick={onJoinClick}
                className="btn-primary text-[11px] font-mono uppercase tracking-[0.16em]"
                data-testid="loyalty-promo-cta"
            >
                Join in 20 seconds →
            </button>
        </div>
    );
};

